import i18n from "@/i18n";

const isEn = () => (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en");

/**
 * Notification helpers for SUX1: a shortened crystal-bell (identical
 * timbre to the splash-screen boot chime) and a soft French-voice
 * "résumé prêt" announcement.
 *
 * Both are gated behind localStorage keys so the user can mute them:
 *   sux1_sound_muted = "1"   → disables bell
 *   sux1_voice_muted = "1"   → disables voice
 *
 * The audio context is lazily created on the first call so we don't
 * fight the browser autoplay policy on cold load. Every call is
 * fire-and-forget and swallows any error to avoid noise in the UI.
 */

let _ctx = null;
function getCtx() {
  if (_ctx) return _ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  } catch (_) {
    return null;
  }
  return _ctx;
}

/**
 * Short (single-strike) version of the splash bell. Uses the same
 * additive-synthesis partials + inharmonic overtones so the ear
 * recognizes it as "the SUX1 chime".
 */
export function playBell() {
  try {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("sux1_sound_muted") === "1") return;
    const ctx = getCtx();
    if (!ctx) return;
    const start = () => {
      // Defensive: iOS may have re-suspended the context between the
      // priming gesture and the async completion of the summary.
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      const master = ctx.createGain();
      master.gain.value = 0.22;
      master.connect(ctx.destination);
      const partials = [
        { mult: 1.0, gain: 1.0, decay: 2.4 },
        { mult: 2.0, gain: 0.45, decay: 1.4 },
        { mult: 3.02, gain: 0.28, decay: 1.0 },
        { mult: 4.1, gain: 0.18, decay: 0.85 },
        { mult: 5.43, gain: 0.09, decay: 0.65 },
      ];
      const fundamental = 1567.98; // G6 — same as the 2nd splash strike
      const t0 = ctx.currentTime + 0.02;
      partials.forEach((p) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = fundamental * p.mult;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(p.gain, t0 + 0.002);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + p.decay);
        osc.connect(g);
        g.connect(master);
        osc.start(t0);
        osc.stop(t0 + p.decay + 0.05);
      });
    };
    if (ctx.state === "suspended") ctx.resume().then(start).catch(() => {});
    else start();
  } catch (_) {}
}

/**
 * Speak the "résumé prêt" (or provided sentence) with the system
 * French voice. Cancels any pending utterance so back-to-back
 * completions don't stack. Chooses the best available French voice.
 */
export function speakSummaryReady(text) {
  try {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("sux1_voice_muted") === "1") return;
    if (!("speechSynthesis" in window)) return;
    // Resolve the sentence: explicit override > user preference > default.
    let sentence = text;
    if (!sentence) {
      try {
        sentence = localStorage.getItem("sux1_voice_phrase") || "";
      } catch (_) {}
    }
    if (!sentence) sentence = isEn() ? "Summary ready" : "Résumé prêt";
    const synth = window.speechSynthesis;
    // Some browsers need voices to be loaded async.
    const speak = () => {
      const voices = synth.getVoices();
      const utter = new SpeechSynthesisUtterance(sentence);
      const en = isEn();
      const fr = en
        ? voices.find((v) => /^en/i.test(v.lang))
        : voices.find(
          (v) =>
            /fr[-_]FR/i.test(v.lang) &&
            /(google|amélie|thomas|virginie|audrey)/i.test(v.name)
        ) || voices.find((v) => /^fr/i.test(v.lang));
      if (fr) utter.voice = fr;
      utter.lang = fr?.lang || (en ? "en-GB" : "fr-FR");
      utter.rate = 1.02;
      utter.pitch = 1.0;
      utter.volume = 0.9;
      synth.cancel();
      synth.speak(utter);
    };
    if (synth.getVoices().length === 0) {
      // voiceschanged fires once the browser has loaded the voices list.
      const onReady = () => {
        speak();
        synth.removeEventListener("voiceschanged", onReady);
      };
      synth.addEventListener("voiceschanged", onReady);
      // Fallback if the event never fires (some Firefox versions).
      setTimeout(speak, 400);
    } else {
      speak();
    }
  } catch (_) {}
}

/**
 * "Prime" the audio + speech APIs while we're still inside a user
 * gesture (e.g. the click on the Résumer button). Without this, mobile
 * browsers (especially iOS Safari) refuse to play any sound triggered
 * asynchronously more than a few seconds after the last interaction.
 *
 * Call this once when the user triggers a long-running action; it
 * warms up the AudioContext and speaks a silent/empty utterance so
 * the synthesizer is authorized to speak again later on completion.
 */
export function primeAudio() {
  try {
    if (typeof window === "undefined") return;
    // 1) Warm up AudioContext (for the crystal bell). iOS Safari
    //    requires us to actually PLAY a node inside the user gesture
    //    (a bare resume() is not enough — the context is re-suspended
    //    as soon as the tab loses focus or the JS event loop idles).
    //    We start a zero-gain oscillator for a few ms so the context
    //    is durably unlocked and can play back the real bell 30 s
    //    later when the summary completes.
    const ctx = getCtx();
    if (ctx) {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      try {
        const g = ctx.createGain();
        g.gain.value = 0.0001; // effectively inaudible
        g.connect(ctx.destination);
        const osc = ctx.createOscillator();
        osc.frequency.value = 440;
        osc.connect(g);
        const t = ctx.currentTime;
        osc.start(t);
        osc.stop(t + 0.05);
      } catch (_) {}
    }
    // 2) Prime SpeechSynthesis with a near-silent utterance so the
    //    browser marks the user gesture as "audio granted" for this
    //    tab. iOS Safari especially loses the permission after a
    //    couple of seconds otherwise.
    if ("speechSynthesis" in window) {
      const synth = window.speechSynthesis;
      // Cancel any pending queue.
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(" ");
      utter.volume = 0.01;
      utter.rate = 2;
      utter.lang = isEn() ? "en-GB" : "fr-FR";
      // Some browsers need us to force a voice load first.
      // Reading getVoices() is enough to trigger the async fetch.
      synth.getVoices();
      synth.speak(utter);
    }
  } catch (_) {}
}


export function notifyOperationComplete(announce = true, sentence) {
  playBell();
  if (announce) speakSummaryReady(sentence);
}
